// services/v3/cache/idb.ts — tiny key/value store (SPEC §5)
// idb-keyval backed, with a localStorage JSON fallback when IndexedDB is
// unavailable or throws. NOTHING in here ever throws to the caller.

import { createStore, get, set, del, keys, type UseStore } from 'idb-keyval'

const DB_NAME = 'xtable-v3'
const STORE_NAME = 'kv'
/** localStorage fallback key prefix */
const LS_PREFIX = 'xtable-v3:'

/** Flips to true once IndexedDB proves unusable; from then on we use localStorage only. */
let fallbackOnly = false
let store: UseStore | null = null

const hasWindow = (): boolean => typeof window !== 'undefined'

function warn(op: string, err: unknown): void {
  try {
    console.warn(`[v3/cache] ${op} failed; degrading to localStorage`, err)
  } catch {
    /* noop */
  }
}

/** Returns the idb-keyval store, or null when IndexedDB cannot be used. */
function getStore(): UseStore | null {
  if (fallbackOnly) return null
  if (!hasWindow()) return null
  try {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      fallbackOnly = true
      return null
    }
  } catch (err) {
    fallbackOnly = true
    warn('indexedDB access', err)
    return null
  }
  if (store) return store
  try {
    store = createStore(DB_NAME, STORE_NAME)
    return store
  } catch (err) {
    fallbackOnly = true
    warn('createStore', err)
    return null
  }
}

/* ------------------------------------------------------------------ */
/* localStorage fallback                                               */
/* ------------------------------------------------------------------ */

function lsGet<T>(key: string): T | undefined {
  if (!hasWindow()) return undefined
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + key)
    if (raw === null) return undefined
    return JSON.parse(raw) as T
  } catch (err) {
    warn(`lsGet(${key})`, err)
    return undefined
  }
}

function lsSet<T>(key: string, value: T): void {
  if (!hasWindow()) return
  try {
    window.localStorage.setItem(LS_PREFIX + key, JSON.stringify(value))
  } catch (err) {
    warn(`lsSet(${key})`, err)
  }
}

function lsDel(key: string): void {
  if (!hasWindow()) return
  try {
    window.localStorage.removeItem(LS_PREFIX + key)
  } catch (err) {
    warn(`lsDel(${key})`, err)
  }
}

function lsKeys(): string[] {
  if (!hasWindow()) return []
  const out: string[] = []
  try {
    const ls = window.localStorage
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i)
      if (k && k.startsWith(LS_PREFIX)) out.push(k.slice(LS_PREFIX.length))
    }
  } catch (err) {
    warn('lsKeys', err)
  }
  return out
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const s = getStore()
  if (s) {
    try {
      const v = await get<T>(key, s)
      if (v !== undefined) return v
      // fall through: value may live in the localStorage fallback (mixed history)
      return lsGet<T>(key)
    } catch (err) {
      fallbackOnly = true
      warn(`kvGet(${key})`, err)
    }
  }
  return lsGet<T>(key)
}

export async function kvSet<T>(key: string, value: T): Promise<void> {
  const s = getStore()
  if (s) {
    try {
      await set(key, value, s)
      return
    } catch (err) {
      fallbackOnly = true
      warn(`kvSet(${key})`, err)
    }
  }
  lsSet(key, value)
}

export async function kvDel(key: string): Promise<void> {
  const s = getStore()
  if (s) {
    try {
      await del(key, s)
    } catch (err) {
      fallbackOnly = true
      warn(`kvDel(${key})`, err)
    }
  }
  // always drop any localStorage twin so a stale fallback copy cannot resurface
  lsDel(key)
}

export async function kvKeys(): Promise<string[]> {
  const out = new Set<string>()
  const s = getStore()
  if (s) {
    try {
      const ks = await keys(s)
      for (const k of ks) if (typeof k === 'string') out.add(k)
    } catch (err) {
      fallbackOnly = true
      warn('kvKeys', err)
    }
  }
  for (const k of lsKeys()) out.add(k)
  return Array.from(out)
}

/** Test/diagnostics helper — true when IndexedDB has been ruled out. */
export const isFallbackOnly = (): boolean => fallbackOnly
